import { useTrans } from '@/shared/hooks/useTranslation';
import { SkipOnboardingModal } from '@/view/features/onboarding/SkipOnboardingModal';
import type { OnboardingStepView } from '@/viewmodel/features/onboarding';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useState } from 'react';

interface OnboardingProgressChecklistProps {
  readonly steps: OnboardingStepView[];
  readonly percent: number;
  readonly title?: string;
}

function StepCircle({ completed, active }: { completed: boolean; active: boolean }) {
  if (completed) {
    return (
      <span className="size-5 rounded-full border-2 border-primary bg-primary flex items-center justify-center shrink-0">
        <svg aria-hidden="true" width="10" height="8" viewBox="0 0 10 8" fill="none">
          <path
            d="M1 4l2.5 2.5L9 1"
            stroke="white"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }

  return (
    <span
      className={`size-5 rounded-full border-2 shrink-0 ${
        active ? 'border-primary' : 'border-muted-foreground/40'
      }`}
    />
  );
}

export function OnboardingProgressChecklist({
  steps,
  percent,
  title,
}: OnboardingProgressChecklistProps) {
  const { t } = useTrans();
  const resolvedTitle = title ?? t('onboarding.checklist.title');
  const [collapsed, setCollapsed] = useState(false);
  const [dismissOpen, setDismissOpen] = useState(false);

  return (
    <>
      <SkipOnboardingModal isOpen={dismissOpen} onClose={() => setDismissOpen(false)} />
      <div className="w-80 bg-surface rounded-2xl shadow-xl border border-surface-secondary overflow-hidden">
        <div className="flex items-center">
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="flex-1 flex items-center justify-between px-5 py-4 hover:bg-surface-secondary/50 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-foreground">{percent}%</span>
              <span className="text-xs font-bold text-foreground">{resolvedTitle}</span>
            </div>
            {collapsed ? (
              <ChevronDown size={14} className="text-muted-foreground" />
            ) : (
              <ChevronUp size={14} className="text-muted-foreground" />
            )}
          </button>
          <button
            type="button"
            aria-label={t('onboarding.dismiss.button')}
            onClick={() => setDismissOpen(true)}
            className="px-3 py-4 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          >
            <X size={14} />
          </button>
        </div>

        {!collapsed && (
          <>
            <div className="h-px bg-surface-secondary mx-0" />
            <ul className="py-3 px-3 flex flex-col gap-1">
              {steps.map((step) => {
                const liClass = step.active ? 'bg-primary/8' : '';
                const labelClass = step.active
                  ? 'text-primary'
                  : step.completed
                    ? 'text-muted-foreground line-through'
                    : 'text-foreground/60';

                return (
                  <li
                    key={step.label}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${liClass}`}
                  >
                    <StepCircle completed={step.completed} active={step.active} />
                    <span className={`text-xs font-medium leading-snug ${labelClass}`}>
                      {step.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </>
  );
}
